/* eslint-disable react/no-children-prop */
import BadgeApplicationDetail from '@component/community/badge-application/BadgeApplicationDetail';
import { BadgeApplicationManageModal } from '@component/community/badge-application/BadgeApplicationManageModal';
import { VerticalDotsIcon } from '@component/icon/VerticalDotsIcon';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from '@nextui-org/react';
import useSuspenseQuery from '@simppl/react-query/useSuspenseQuery';
import { badgeApi } from '@simppl/repository-admin/badge';
import { ApplicationTypeEnum } from '@simppl/repository-admin/badge/res';
import { ApplicationStatusEnum } from '@simppl/repository-admin/tutor/res';
import { Search } from 'lucide-react';
import { useState } from 'react';

const APPLICATION_STATUS_NAME = {
  [ApplicationStatusEnum.APPROVED]: '승인',
  [ApplicationStatusEnum.REJECTED]: '반려',
  [ApplicationStatusEnum.PENDING]: '대기',
};

const APPLICATION_TYPE_NAME = {
  [ApplicationTypeEnum.NEW]: '생성',
  [ApplicationTypeEnum.MODIFY]: '수정',
};

const columns = [
  { key: 'id', label: 'id' },
  { key: 'user', label: '신청자' },
  { key: 'applicationType', label: '신청 구분' },
  { key: 'status', label: '승인 상태' },
  { key: 'rejectReason', label: '반려 사유' },
  { key: 'createdAt', label: '신청 일자' },
  { key: 'updatedAt', label: '승인/반려 일자' },
  { key: 'settings', label: '액션' },
];

export default function BadgeApplicationPage() {
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  const [inputValue, setInputValue] = useState('');
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(0);

  const {
    data: { totalCount, applications },
  } = useSuspenseQuery({
    queryFn: () => badgeApi.APPLICATION.GET({ page, size: 10, keyword: search }),
    queryKey: ['badgeApi.APPLICATION.GET', page, search],
  });

  const { isOpen, onOpenChange } = useDisclosure();
  const [manageAppliationId, setManageApplicationId] = useState('');

  return (
    <div className='flex min-h-[80%] flex-col'>
      {isOpen && (
        <BadgeApplicationManageModal
          applicationId={manageAppliationId}
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          invalidateKeys={['badgeApi.APPLICATION.GET', page, search]}
        />
      )}
      <Card className='grow'>
        <CardHeader>
          <h1 className='text-2xl'>뱃지 등록/수정 신청 관리</h1>
        </CardHeader>
        <Divider />
        <CardBody className='flex flex-col gap-4'>
          <div className='flex flex-col gap-4'>
            <div className='flex w-full flex-row justify-between'>
              <div className='flex max-w-sm flex-1 flex-row items-center'>
                <Input
                  labelPlacement='outside'
                  startContent={<Search />}
                  value={inputValue}
                  onValueChange={setInputValue}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSearch(inputValue);
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className='flex gap-4'>
            <Table layout='auto'>
              <TableHeader columns={columns}>
                {(column) => <TableColumn key={column.key}>{column.label}</TableColumn>}
              </TableHeader>
              <TableBody>
                {applications
                  .map((application) => {
                    const isCreateApplication = application.type === ApplicationTypeEnum.NEW;
                    const elements = [
                      <TableRow
                        key={application.id}
                        onClick={() => {
                          setSelectedItem((prev) =>
                            prev === application.id ? null : application.id,
                          );
                        }}
                        className='cursor-pointer hover:opacity-30'
                      >
                        <TableCell>{application.id}</TableCell>
                        <TableCell>{application.user.name}</TableCell>
                        <TableCell>{APPLICATION_TYPE_NAME[application.type]}</TableCell>
                        <TableCell>{APPLICATION_STATUS_NAME[application.status]}</TableCell>
                        <TableCell>{application.rejectReason}</TableCell>
                        <TableCell>{application.createAt}</TableCell>
                        <TableCell>
                          {application.status !== ApplicationStatusEnum.PENDING
                            ? application.updatedAt
                            : ''}
                        </TableCell>

                        <TableCell>
                          <Dropdown>
                            <DropdownTrigger>
                              <Button
                                isIconOnly
                                size='sm'
                                variant='light'
                              >
                                <VerticalDotsIcon className='text-default-300' />
                              </Button>
                            </DropdownTrigger>
                            <DropdownMenu>
                              <DropdownItem
                                key='edit'
                                onPress={() => {
                                  if (application.status !== ApplicationStatusEnum.PENDING) {
                                    alert('승인/반려가 이미 되었습니다');
                                    return;
                                  }
                                  onOpenChange();
                                  setManageApplicationId(application.id);
                                }}
                                className='text-blue-500'
                              >
                                승인 / 반려
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </TableCell>
                      </TableRow>,
                    ];

                    if (selectedItem === application.id) {
                      elements.push(
                        <TableRow key={`${application.id}content`}>
                          <TableCell colSpan={8}>
                            <BadgeApplicationDetail application={application} />
                          </TableCell>

                          <TableCell
                            className='hidden'
                            children={undefined}
                          />

                          <TableCell
                            className='hidden'
                            children={undefined}
                          />
                          <TableCell
                            className='hidden'
                            children={undefined}
                          />
                          <TableCell
                            className='hidden'
                            children={undefined}
                          />
                          <TableCell
                            className='hidden'
                            children={undefined}
                          />
                          <TableCell
                            className='hidden'
                            children={undefined}
                          />
                          <TableCell
                            className='hidden'
                            children={undefined}
                          />
                        </TableRow>,
                      );
                    }
                    return elements;
                  })
                  .flat()}
              </TableBody>
            </Table>
          </div>
        </CardBody>
        <CardFooter>
          <div className='flex w-full flex-col items-center'>
            <Pagination
              isCompact
              showControls
              showShadow
              page={page}
              onChange={setPage}
              total={Math.ceil(totalCount / 10)}
            />
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
